<template>
  <div class=" mt-4 mb-5">
    <InvoiceRibbon
        @new="openModal"
        @edit="editInvoice"
        @copy="copyInvoice"
        @delete="deleteInvoice"
        @download="downloadInvoice"
        @export="handleExportSelection"
        @import="openImportModal"
        @refresh="fetchInvoices"
        @view="viewInvoice"
        @validate="validateInvoice"
        :isInvoiceSelected="selectedInvoice !== null"
        :is-zugferd-generated="!!selectedInvoice?.fileId"
        :export-options="availableExportOptions"
        :can-create="canCreateInvoices"
        :can-edit="canEditInvoices"
        :can-copy="canCopyInvoices && !copyLoading"
        :can-delete="canDeleteInvoices"
        :can-download="canDownloadFiles"
        :can-export="canExportInvoices"
        :can-import="canImportInvoices"
        :can-view="canViewZugferd"
        :can-validate="canValidateZugferd"
        :can-refresh="canReadZugferd"
    />
    <div v-if="selectedInvoice && canViewPaymentStatus" class="mx-4 mt-3 alert alert-light border" role="status">
      <strong>OPOS-Status für den ausgewählten Rechnungsdatensatz:</strong>
      <span v-if="paymentStatusLoading" class="ms-2">Wird geladen…</span>
      <span v-else-if="selectedInvoicePaymentStatus?.needsReview" class="ms-2 text-warning">Rechnung wurde nach der OPOS-Aufnahme geändert. Zuordnungen prüfen.</span>
      <span v-else-if="selectedInvoicePaymentStatus?.isTracked" class="ms-2">
        {{ paymentStatusLabel(selectedInvoicePaymentStatus.status) }} · Rest
        {{ formatPaymentAmount(selectedInvoicePaymentStatus.remainingAmount, selectedInvoicePaymentStatus.currencyCode) }}
      </span>
      <span v-else-if="selectedInvoicePaymentStatus" class="ms-2">Noch nicht als offener Posten aufgenommen.</span>
      <span v-else class="ms-2">Status nicht verfügbar.</span>
      <button v-if="canRegisterPaymentInvoice && selectedInvoicePaymentStatus && !selectedInvoicePaymentStatus.isTracked"
              type="button" class="btn btn-outline-primary btn-sm ms-3" :disabled="paymentStatusLoading"
              @click="registerSelectedInvoiceForOpos">Als offenen Posten aufnehmen</button>
    </div>
    <InvoiceTable
        :invoices="invoices"
        :current-page="currentPage"
        :max-invoices="maxInvoices"
        :pageCount="pageCount"
        :maxCount="maxCount"
        :orderColumn="orderColumn"
        :orderDirection="orderDirection"
        @select="selectInvoice"
        @fetch="fetchInvoices"
        @set-page="setPage"
        @set-max="setMax"
        @apply-filters="applyFilters"
        @clear-filters="clearFilters"
        @sort-changed="setSort"
        @edit="editInvoice"
    />
    <InvoiceModal
        v-if="showModal"
        :zugferd="selectedInvoice"
        :isNew="isNew"
        :initial-draft="invoiceCopyDraft"
        :copy-source-number="copySourceNumber"
        :saving="invoiceSaving"
        @formSubmitted="handleSubmit"
        @close="closeModal"
    />

    <ViewInvoiceModal
        v-if="showViewModal"
        :invoiceHtml="invoiceHtml"
        @close="showViewModal = false"
    />

    <ValidateInvoiceModal
        v-if="showValidateModal"
        :validation-result-html="validationResultHtml"
        :validator-mode="selectedValidationMode"
        :validator-options="validationModeOptions"
        :validation-metadata="validationMetadata"
        @validator-mode-changed="onValidatorModeChanged"
        @validate-with-mode="validateInvoice"
        @close="closeValidationModal"
    />

    <OpenOfficeExportModal
        v-if="showOpenOfficeExportModal"
        @close="closeOpenOfficeExportModal"
        @export="exportOpenOffice"
    />

    <ExcelExportModal
        v-if="showExcelExportModal"
        @close="closeExcelExportModal"
        @export="exportExcel"
    />

    <InvoiceImportModal
        v-if="showImportModal"
        @close="closeImportModal"
        @import="importDocumentToZugferd"
    />
  </div>
</template>

<script>
import InvoiceRibbon from "../components/Invoice/InvoiceRibbon.vue";
import InvoiceTable from "../components/Invoice/InvoiceTable.vue";
import InvoiceModal from "../components/Invoice/InvoiceModal.vue";
import ViewInvoiceModal from "@/components/Invoice/ViewInvoiceModal.vue";
import ZUGFeRDService from "../services/ZUGFeRDService";
import {useUxStore} from "@/stores/uxStore";
import {useMasterDataStore} from "@/stores/masterDataStore";
import ZUGFeRDListItem from "@/models/ZUGFeRDListItem";
import ValidateInvoiceModal from "@/components/Invoice/ValidateInvoiceModal.vue";
import OpenOfficeExportModal from "@/components/Invoice/OpenOfficeExportModal.vue";
import ExcelExportModal from "@/components/Invoice/ExcelExportModal.vue";
import InvoiceImportModal from "@/components/Invoice/InvoiceImportModal.vue";
import FileTemplateService from "@/services/FileTemplateService";
import InvoiceDocumentService from "@/services/InvoiceDocumentService";
import BankingOposService from "@/services/BankingOposService";
import { useUserStore } from "@/stores/userStore";
import { useEntitlementStore } from "@/stores/entitlementStore";
import { useCompanyFeatureStore } from "@/stores/companyFeatureStore";
import { createInvoiceDraftCopy, invoiceDraftPayload, hasNewInvoiceNumber } from "@/utils/invoiceCopy.cjs";

const disableAuthForE2E = process.env.VUE_APP_DISABLE_AUTH === "true";

export default {
  name: "InvoiceListView",
  components: {InvoiceImportModal, ExcelExportModal, OpenOfficeExportModal, ValidateInvoiceModal, ViewInvoiceModal, InvoiceRibbon, InvoiceTable, InvoiceModal},

  data() {
    return {
      invoices: [],
      currentPage: 1,
      maxInvoices: 15,
      maxCount: 0,
      pageCount: 0,
      isNew: false,
      invoiceCopyDraft: null,
      copySourceNumber: "",
      copyLoading: false,
      invoiceSaving: false,
      showModal: false,
      showViewModal: false,
      showValidateModal: false,
      showOpenOfficeExportModal: false,
      showExcelExportModal: false,
      showImportModal: false,
      pendingValidateAfterImportInvoiceId: null,
      availableExportOptions: [
        { id: "openoffice", label: "OpenOffice", disabled: false },
        { id: "excel", label: "Excel", disabled: false },
      ],
      selectedInvoice: null,
      selectedInvoicePaymentStatus: null,
      paymentStatusLoading: false,
      invoiceHtml: '',
      validationResultHtml: '',
      selectedValidationMode: "kositdaemon",
      validationModeOptions: ["kositdaemon", "servicebw", "internal", "auto"],
      validationMetadata: {
        requestedMode: "",
        usedMode: "",
        usedProvider: "",
        fallbackUsed: false,
      },
      // Filter arrays
      invoiceIdsFilter: [],
      invoiceNumbersFilter: [],
      zugferdversionFilter: "",
      zugferdpfrofileFilter: "",
      searchTerm: "",
      buyerNamesFilter: [],
      fileNamesFilter: [],
      invoiceDateFilter: {start: null, end: null},
      creationDateFilter: {start: null, end: null},
      orderColumn: '',  // default sort column
      orderDirection: '',        // default sort direction

      uxStore: useUxStore(),
      userStore: useUserStore(),
      entitlementStore: useEntitlementStore(),
      companyFeatureStore: useCompanyFeatureStore(),
    };
  },

  beforeCreate() {
    this.masterDataStore = useMasterDataStore();
    this.uxStore = useUxStore();
    this.userStore = useUserStore();
    this.entitlementStore = useEntitlementStore();
    this.companyFeatureStore = useCompanyFeatureStore();
  },
  computed: {
    canViewPaymentStatus() {
      return this.companyFeatureStore.isEnabled("banking-opos")
        && this.entitlementStore.hasAllModules(["opos", "invoicing"])
        && this.userStore.hasPermission("opos.read")
        && this.userStore.hasPermission("invoices.read");
    },
    canRegisterPaymentInvoice() {
      return this.canViewPaymentStatus && this.userStore.hasPermission("opos.manage");
    },
    canReadZugferd() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("e-invoice")
        && this.userStore.hasPermission("zugferd.read");
    },
    canCreateInvoices() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("invoicing")
        && this.userStore.hasPermission("invoices.write");
    },
    canCopyInvoices() {
      return this.canCreateInvoices && this.canReadZugferd
        && this.userStore.hasPermission("zugferd.read")
        && this.userStore.hasPermission("zugferd.write");
    },
    canEditInvoices() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("invoicing")
        && this.userStore.hasPermission("invoices.write");
    },
    canDeleteInvoices() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("invoicing")
        && this.userStore.hasPermission("invoices.delete");
    },
    canExportInvoices() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("invoicing")
        && this.userStore.hasPermission("invoices.export");
    },
    canImportInvoices() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasAllModules(["invoicing", "e-invoice"])
        && this.userStore.hasPermission("invoices.import");
    },
    canDownloadFiles() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("invoicing")
        && this.userStore.hasPermission("files.read");
    },
    canViewZugferd() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("e-invoice")
        && this.userStore.hasPermission("transform.execute");
    },
    canValidateZugferd() {
      if (disableAuthForE2E) return true;
      return this.entitlementStore.hasModule("e-invoice")
        && this.userStore.hasPermission("zugferd.validate");
    },
  },

  methods: {
    async fetchInvoices() {

      try {
        const params = {
          page: this.currentPage,
          pageSize: this.maxInvoices,
        };

        if (this.invoiceIdsFilter.length > 0) {
          params['InvoiceFilter.InvoiceIds'] = this.invoiceIdsFilter;
        }
        if (this.invoiceNumbersFilter.length > 0) {
          params['InvoiceFilter.InvoiceNumbers'] = this.invoiceNumbersFilter;
        }
        if (this.buyerNamesFilter.length > 0) {
          params['InvoiceFilter.BuyerNames'] = this.buyerNamesFilter;
        }

        if (this.fileNamesFilter.length > 0) {
          params['FileNames'] = this.fileNamesFilter;
        }

        if (this.zugferdversionFilter) {
          params['ZUGFeRDVersionIds'] = this.zugferdversionFilter;
        }

        if (this.zugferdpfrofileFilter) {
          params['ZUGFeRDProfileIds'] = this.zugferdpfrofileFilter;
        }

        if (this.searchTerm) {
          params['SearchTerm'] = this.searchTerm;
        }


        if (this.invoiceDateFilter.start && this.invoiceDateFilter.end) {
          params['InvoiceFilter.StartDate'] = this.invoiceDateFilter.start;
          params['InvoiceFilter.EndDate'] = this.invoiceDateFilter.end;
        }


        if (this.creationDateFilter.start && this.creationDateFilter.end) {
          params['StartDate'] = this.creationDateFilter.start;
          params['EndDate'] = this.creationDateFilter.end;
        }


        if (this.orderColumn) {
          params.SortColumn = this.orderColumn;
          params.SortOrder = this.orderDirection;
        }

        const response = await ZUGFeRDService.getZUGFeRDs(params);


        this.invoices = response.data.items.map(zugferdData =>
            zugferdData instanceof ZUGFeRDListItem ? zugferdData : new ZUGFeRDListItem(zugferdData)
        );
        this.maxCount = response.data.maxCount;
        this.pageCount = response.data.pageCount;
      } catch (error) {
        console.error("Error fetching invoices:", error);
        if (error.response) {
          this.uxStore.setError(error.response.data);
        } else if (error.request) {
          this.uxStore.setError("No response from server. Please try again later.");
        } else {
          this.uxStore.setError("An unexpected error occurred: " + error.message);
        }
      }

    },
    async fetchMasterData() {
      await this.masterDataStore.fetchAllMasterData();
    },
    setPage(page) {
      this.currentPage = page;
      this.fetchInvoices();
    },
    setMax(max) {
      this.maxInvoices = max;
      this.currentPage = 1;
      this.fetchInvoices();
    },
    setSort({column}) {
      if (this.orderColumn === column) {
        // Toggle direction if same column is clicked
        this.orderDirection = this.orderDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.orderColumn = column;
        this.orderDirection = 'desc';
      }
      this.fetchInvoices();
    },
    openModal() {
      this.invoiceCopyDraft = null;
      this.copySourceNumber = "";
      this.isNew = true;
      this.showModal = true;
      this.selectedInvoice = null;
    },
    closeModal(keepInvoiceSelected) {
      this.showModal = false;
      this.invoiceCopyDraft = null;
      this.copySourceNumber = "";
      this.fetchInvoices();
      this.uxStore.stopLoading();
      this.uxStore.clearParsingErrors();
      useMasterDataStore().clearSelectedCountryCodes();
      if (!keepInvoiceSelected) {
        this.selectedInvoice = null;
      }
    },

    async selectInvoice(invoice) {
      this.selectedInvoice = invoice;
      this.selectedInvoicePaymentStatus = null;
      if (!invoice || !this.canViewPaymentStatus) return;
      const companyId = this.userStore.currentCompanyId;
      this.paymentStatusLoading = true;
      try {
        const invoiceId = await this.resolveSelectedInvoiceId();
        if (invoiceId && this.selectedInvoice === invoice && this.userStore.currentCompanyId === companyId) {
          const status = await BankingOposService.getInvoicePaymentStatus(invoiceId);
          if (this.selectedInvoice === invoice && this.userStore.currentCompanyId === companyId) this.selectedInvoicePaymentStatus = status;
        }
      } catch {
        if (this.selectedInvoice === invoice && this.userStore.currentCompanyId === companyId) this.selectedInvoicePaymentStatus = null;
      } finally {
        if (this.selectedInvoice === invoice) this.paymentStatusLoading = false;
      }
    },
    paymentStatusLabel(status) {
      return { Open: "Offen", PartiallyPaid: "Teilbezahlt", Paid: "Bezahlt", Overpaid: "Überzahlt" }[status] || "Unbekannt";
    },
    formatPaymentAmount(amount, currencyCode = "EUR") {
      return new Intl.NumberFormat("de-DE", { style: "currency", currency: currencyCode || "EUR" }).format(Number(amount || 0));
    },
    async registerSelectedInvoiceForOpos() {
      const selected = this.selectedInvoice;
      if (!selected || !this.canRegisterPaymentInvoice) return;
      const companyId = this.userStore.currentCompanyId;
      this.paymentStatusLoading = true;
      try {
        const invoiceId = await this.resolveSelectedInvoiceId();
        if (!invoiceId || this.selectedInvoice !== selected || this.userStore.currentCompanyId !== companyId) return;
        await BankingOposService.registerInvoice(invoiceId);
        const status = await BankingOposService.getInvoicePaymentStatus(invoiceId);
        if (this.selectedInvoice === selected && this.userStore.currentCompanyId === companyId) this.selectedInvoicePaymentStatus = status;
      } catch {
        this.uxStore.setError("Der Rechnungsdatensatz konnte nicht als offener Posten aufgenommen werden.");
      } finally {
        if (this.selectedInvoice === selected) this.paymentStatusLoading = false;
      }
    },
    async viewInvoice() {
      if (this.selectedInvoice && this.selectedInvoice.fileId) {
        try {
          this.uxStore.startLoading();
          const response = await ZUGFeRDService.viewZUGFeRD(this.selectedInvoice.fileId);
          const blob = new Blob([response.data], {type: "text/html"});
          const reader = new FileReader();
          reader.onload = () => {
            this.invoiceHtml = reader.result;
            this.showViewModal = true;
            this.uxStore.stopLoading();
          };
          reader.readAsText(blob);
        } catch (error) {
          console.error("Error viewing invoice:", error);
          this.uxStore.stopLoading();
        }
      } else {
        console.warn("No selected invoice or file ID.");
        this.uxStore.setError("No selected invoice or file ID.");
      }
    },
    openOpenOfficeExportModal() {
      if (!this.selectedInvoice) {
        this.uxStore.setError("Bitte zuerst eine Rechnung auswählen.");
        return;
      }

      this.showOpenOfficeExportModal = true;
    },
    openExcelExportModal() {
      if (!this.selectedInvoice) {
        this.uxStore.setError("Bitte zuerst eine Rechnung auswählen.");
        return;
      }

      this.showExcelExportModal = true;
    },
    handleExportSelection(exportType) {
      switch (exportType) {
        case "openoffice":
          this.openOpenOfficeExportModal();
          break;
        case "excel":
          this.openExcelExportModal();
          break;
        default:
          this.uxStore.setError("Unbekannter Exporttyp.");
          break;
      }
    },
    closeOpenOfficeExportModal() {
      this.showOpenOfficeExportModal = false;
    },
    closeExcelExportModal() {
      this.showExcelExportModal = false;
    },
    closeImportModal() {
      this.showImportModal = false;
    },
    async resolveSelectedInvoiceId() {
      if (!this.selectedInvoice) {
        return null;
      }

      if (this.selectedInvoice.invoice?.id) {
        return this.selectedInvoice.invoice.id;
      }

      if (!this.selectedInvoice.id) {
        return null;
      }

      const zugferdDetails = await ZUGFeRDService.getZUGFeRD(this.selectedInvoice.id);
      return zugferdDetails?.invoice?.id || null;
    },
    async exportOpenOffice(payload) {
      try {
        this.uxStore.startLoading();

        const invoiceId = await this.resolveSelectedInvoiceId();
        if (!invoiceId) {
          this.uxStore.setError("Die zugehörige Rechnungs-ID konnte nicht ermittelt werden.");
          return;
        }

        const exportResponse = await FileTemplateService.exportInvoiceToOpenOffice(
            invoiceId,
            payload.templateFileId,
            payload.outputFileName,
            payload.runAiReview,
            payload.autoFixOpenOfficeDocument,
            payload.chatGptModel
        );

        const exportResult = exportResponse?.data;
        if (!exportResult?.fileId) {
          this.uxStore.setError("Der Export wurde ohne Datei-ID zurückgegeben.");
          return;
        }

        await FileTemplateService.downloadFile(exportResult.fileId, exportResult.fileName || "invoice-export.odt");
      } catch (error) {
        console.error("Error exporting OpenOffice invoice:", error);
      } finally {
        this.uxStore.stopLoading();
      }
    },
    openImportModal() {
      if (!this.selectedInvoice) {
        this.uxStore.setError("Bitte zuerst eine Rechnung auswählen.");
        return;
      }

      this.showImportModal = true;
    },
    async importDocumentToZugferd(payload) {
      try {
        this.uxStore.startLoading();

        const invoiceId = await this.resolveSelectedInvoiceId();
        if (!invoiceId) {
          this.uxStore.setError("Die zugehörige Rechnungs-ID konnte nicht ermittelt werden.");
          return;
        }

        const conversionResponse = await InvoiceDocumentService.importDocumentToZugferd(invoiceId, {
          documentFileId: payload.documentFileId,
          zugferdVersionId: payload.zugferdVersionId,
          zugferdProfileId: payload.zugferdProfileId,
          runAiReview: payload.runAiReview,
          autoFixOpenOfficeDocument: payload.autoFixOpenOfficeDocument,
          chatGptModel: payload.chatGptModel,
        });

        const conversionResult = conversionResponse?.data;
        const zugferdFileId = conversionResult?.zugferdFileId || conversionResult?.zugFeRDFileId || conversionResult?.zUGFeRDFileId;
        const zugferdFileName = conversionResult?.zugferdFileName || conversionResult?.zugFeRDFileName || conversionResult?.zUGFeRDFileName;

        if (!zugferdFileId) {
          this.uxStore.setError("Die Konvertierung hat keine ZUGFeRD-Datei geliefert.");
          return;
        }

        // Keep selected invoice state in sync (draft -> generated file).
        if (this.selectedInvoice) {
          this.selectedInvoice.fileId = zugferdFileId;
          if (zugferdFileName) {
            this.selectedInvoice.fileName = zugferdFileName;
          }

          const zugferdId =
            conversionResult?.zugferdId || conversionResult?.zugFeRDId || conversionResult?.zUGFeRDId;
          if (zugferdId) {
            this.selectedInvoice.id = zugferdId;
          }
        }

        await FileTemplateService.downloadFile(zugferdFileId, zugferdFileName || "zugferd-invoice.pdf");
        await this.fetchInvoices();
        this.closeImportModal();

        if (this.pendingValidateAfterImportInvoiceId && this.pendingValidateAfterImportInvoiceId === invoiceId) {
          this.pendingValidateAfterImportInvoiceId = null;
          await this.validateInvoice();
        }
      } catch (error) {
        console.error("Error importing document / converting to ZUGFeRD:", error);
      } finally {
        this.uxStore.stopLoading();
        await this.userStore.loadMe().catch(() => undefined);
      }
    },
    async exportExcel(payload) {
      try {
        this.uxStore.startLoading();

        const invoiceId = await this.resolveSelectedInvoiceId();
        if (!invoiceId) {
          this.uxStore.setError("Die zugehörige Rechnungs-ID konnte nicht ermittelt werden.");
          return;
        }

        const exportResponse = await FileTemplateService.exportInvoiceToExcel(
            invoiceId,
            payload.templateFileId,
            payload.outputFileName
        );

        const exportResult = exportResponse?.data;
        if (!exportResult?.fileId) {
          this.uxStore.setError("Der Export wurde ohne Datei-ID zurückgegeben.");
          return;
        }

        await FileTemplateService.downloadFile(exportResult.fileId, exportResult.fileName || "invoice-export.xlsx");
      } catch (error) {
        console.error("Error exporting Excel invoice:", error);
      } finally {
        this.uxStore.stopLoading();
      }
    },
    onValidatorModeChanged(validatorMode) {
      this.selectedValidationMode = validatorMode || "kositdaemon";
    },
    async validateInvoice(validatorMode = this.selectedValidationMode) {
      if (this.selectedInvoice && this.selectedInvoice.fileId) {
        try {
          this.uxStore.startLoading();
          const response = await ZUGFeRDService.validateZUGFeRD(this.selectedInvoice.id, validatorMode);
          this.validationResultHtml = response.data;
          const responseHeaders = response.headers || {};
          this.validationMetadata = {
            requestedMode: responseHeaders["x-validation-requested-mode"] || "Auto",
            usedMode: responseHeaders["x-validation-used-mode"] || "Unknown",
            usedProvider: responseHeaders["x-validation-used-provider"] || "Unknown",
            fallbackUsed: String(responseHeaders["x-validation-fallback-used"] || "false").toLowerCase() === "true",
          };

          this.showValidateModal = true;
          this.uxStore.stopLoading();

        } catch (error) {
          console.error("Error Validating invoice:", error);
          this.uxStore.stopLoading();
        }
      } else if (this.selectedInvoice) {
        // Draft invoices don't have a ZUGFeRD PDF yet. Guide the user through export + import first.
        const invoiceId = await this.resolveSelectedInvoiceId();
        this.pendingValidateAfterImportInvoiceId = invoiceId;
        this.openOpenOfficeExportModal();
      } else {
        console.warn("No selected invoice.");
        this.uxStore.setError("Bitte zuerst eine Rechnung auswählen.");
      }
    },
    closeValidationModal(keepInvoiceSelected) {
      this.showValidateModal = false;
      this.validationResultHtml = "";
      this.validationMetadata = {
        requestedMode: "",
        usedMode: "",
        usedProvider: "",
        fallbackUsed: false,
      };
      this.fetchInvoices();
      this.uxStore.stopLoading();
      if (!keepInvoiceSelected) {
        this.selectedInvoice = null;
      }
    },
    async copyInvoice() {
      if (!this.canCopyInvoices || !this.selectedInvoice || this.copyLoading) return;
      const sourceId = this.selectedInvoice.id;
      this.copyLoading = true;
      try {
        const source = await ZUGFeRDService.getZUGFeRD(sourceId);
        if (!this.canCopyInvoices || this.selectedInvoice?.id !== sourceId) return;
        this.invoiceCopyDraft = createInvoiceDraftCopy(source);
        this.copySourceNumber = source.invoice.invoiceNumber || "";
        this.isNew = true;
        this.showModal = true;
      } catch (error) {
        this.uxStore.setError("Die Rechnung konnte nicht als Vorlage geladen werden.");
      } finally {
        this.copyLoading = false;
      }
    },
    async editInvoice() {
      this.invoiceCopyDraft = null;
      this.copySourceNumber = "";
      if (this.selectedInvoice) {
        console.log("SELECTED INVOICE", this.selectedInvoice);
        try {
          const data = await ZUGFeRDService.getZUGFeRD(this.selectedInvoice.id);
          if (data) {
            this.isNew = false;
            this.selectedInvoice = data;
            this.showModal = true;
          } else {
            console.warn("No data returned for selected invoice.");
          }
        } catch (error) {
          console.error("Error fetching invoice details:", error);
        }
      }
    },
    async deleteInvoice() {
      if (this.selectedInvoice && confirm("Are you sure you want to delete this invoice?")) {
        try {
          await ZUGFeRDService.deleteZUGFeRD(this.selectedInvoice.id);
          this.selectedInvoice = null;
          this.fetchInvoices();
        } catch (error) {
          console.error("Error deleting invoice:", error);
        }
      }
    },
    async downloadInvoice() {
      if (this.selectedInvoice && this.selectedInvoice.fileId) {
        try {
          const response = await ZUGFeRDService.downloadZUGFeRD(this.selectedInvoice.fileId);
          const contentDisposition = response.headers['content-disposition'];
          let filename = `${this.selectedInvoice.fileId}.pdf`;

          if (contentDisposition) {
            const filenameUtf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/);
            if (filenameUtf8Match) {
              filename = decodeURIComponent(filenameUtf8Match[1]);
            } else {
              const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
              if (filenameMatch) {
                filename = filenameMatch[1];
              }
            }
          }

          const contentType = response.headers['content-type'] || 'application/octet-stream';
          const blob = new Blob([response.data], {type: contentType});
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Error downloading invoice:", error);
        }
      } else if (this.selectedInvoice) {
        this.uxStore.setError("Diese Rechnung hat noch keine ZUGFeRD-Datei. Bitte zuerst importieren/umwandeln.");
      }
    },
    async handleSubmit(invoiceData, action = 'save') {
      if (this.invoiceSaving) return;
      if (this.invoiceCopyDraft) {
        if (!this.canCopyInvoices) return;
        if (!hasNewInvoiceNumber(invoiceData?.invoice?.invoiceNumber, this.copySourceNumber)) {
          this.uxStore.setError("Bitte eine neue Rechnungsnummer für die Kopie eingeben.");
          return;
        }
        invoiceData = invoiceDraftPayload(invoiceData);
      }
      this.invoiceSaving = true;
      //console.log("Invoice data to submit:", invoiceData, "Action:", action);

      try {
        let response;
        if (!this.isNew) {
          // Editing an existing invoice
          response = await ZUGFeRDService.editZUGFeRD(this.selectedInvoice.id, invoiceData);
        } else {
          // Creating a new invoice
          response = await ZUGFeRDService.createZUGFeRD(invoiceData);
        }

        // Only proceed if the response is successful (HTTP 2xx)
        if (response && response.status >= 200 && response.status < 300) {
          // If action is 'saveAndValidate', perform validation before closing
          if (action === 'saveAndValidate') {
            if (this.isNew && response.data.id) {
              // For new invoices, we need to get the full invoice details first
              const newInvoiceData = await ZUGFeRDService.getZUGFeRD(response.data.id);
              this.selectedInvoice = newInvoiceData;
            }

            // Now validate using the selectedInvoice which is properly set
            this.closeModal(true);

            if (this.selectedInvoice?.fileId) {
              await this.validateInvoice();
            } else {
              // Draft invoices need export + import before validation.
              const invoiceId = await this.resolveSelectedInvoiceId();
              this.pendingValidateAfterImportInvoiceId = invoiceId;
              this.openOpenOfficeExportModal();
            }
          } else {
            // For normal save, just close the modal
            this.closeModal();
          }
        } else {
          console.error("Invoice creation/update failed:", response);
        }
      } catch (error) {
        console.error("Error submitting invoice:", error);
      } finally {
        this.invoiceSaving = false;
        await this.userStore.loadMe().catch(() => undefined);
      }
    },
    applyFilters({
                   invoiceNumbers,
                   buyerNames,
                   fileNames,
                   invoiceDateRange,
                   creationDateRange,
                   zugferdVersion,
                   zugferdProfile,
                   searchTerm
                 }) {
      // Update parent's filter arrays
      this.invoiceNumbersFilter = invoiceNumbers;
      this.buyerNamesFilter = buyerNames;
      this.fileNamesFilter = fileNames;
      this.zugferdversionFilter = zugferdVersion;
      this.zugferdpfrofileFilter = zugferdProfile;
      this.searchTerm = searchTerm;
      this.invoiceDateFilter = invoiceDateRange;
      this.creationDateFilter = creationDateRange;
      this.selectedInvoice = null;

      this.currentPage = 1;


      this.fetchInvoices();
    },

    clearFilters() {
      this.invoiceNumbersFilter = [];
      this.buyerNamesFilter = [];
      this.fileNamesFilter = [];
      this.invoiceIdsFilter = [];
      this.fileNamesFilter = [];
      this.invoiceDateFilter = {start: null, end: null};
      this.creationDateFilter = {start: null, end: null};
      this.zugferdversionFilter = "";
      this.zugferdpfrofileFilter = "";
      this.searchTerm = "";
      this.selectedInvoice = null;
      this.fetchInvoices();
    }
  },

  mounted() {
    if (!this.userStore.me) this.userStore.loadMe().catch(() => undefined);
    this.fetchInvoices();
    this.masterDataStore.fetchAllMasterData();
  },
};
</script>

